import { PDFDocument } from "pdf-lib";

const A4 = { width: 595.28, height: 841.89 };

const readAsBytes = async (file: File): Promise<Uint8Array> => {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
};

const isPdf = (file: File): boolean =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

const isJpg = (file: File): boolean => {
  const n = file.name.toLowerCase();
  return file.type === "image/jpeg" || n.endsWith(".jpg") || n.endsWith(".jpeg");
};

const isPng = (file: File): boolean =>
  file.type === "image/png" || file.name.toLowerCase().endsWith(".png");

const timestamp = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  // Incluye segundos y un sufijo aleatorio para que dos uniones seguidas
  // nunca generen el mismo nombre (se detectarían como duplicado por nombre).
  const rand = Math.random().toString(36).slice(2, 6);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}_${rand}`;
};

/**
 * Combina PDFs e imágenes (JPG/PNG) en un único PDF.
 * Las imágenes se colocan escaladas dentro de una página A4 vertical.
 */
export const mergeFilesToPdf = async (files: File[]): Promise<File> => {
  if (files.length === 0) throw new Error("Sin archivos que unir");

  const out = await PDFDocument.create();

  for (const file of files) {
    const bytes = await readAsBytes(file);
    if (isPdf(file)) {
      const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const pages = await out.copyPages(src, src.getPageIndices());
      pages.forEach((p) => out.addPage(p));
    } else if (isJpg(file) || isPng(file)) {
      const img = isJpg(file)
        ? await out.embedJpg(bytes)
        : await out.embedPng(bytes);
      const page = out.addPage([A4.width, A4.height]);
      const scale = Math.min(A4.width / img.width, A4.height / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      page.drawImage(img, {
        x: (A4.width - w) / 2,
        y: (A4.height - h) / 2,
        width: w,
        height: h,
      });
    } else {
      throw new Error(`Formato no compatible para unir: ${file.name}`);
    }
  }

  const bytes = await out.save();
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  return new File([blob], `unido_${timestamp()}.pdf`, { type: "application/pdf" });
};
